import { type FormEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  Input,
  DatePickerInput,
  Controller,
  AutoComplete,
  WorkTimeFields,
  splitWorkTime,
  updateWorkTimePart,
  type WorkTimePart,
} from "#components/input";
import { Modal, ConfirmationModal, type ModalProps } from "#components/modal";
import { useForm } from "react-hook-form";
import {
  array,
  check,
  maxLength,
  nullable,
  number,
  object,
  optional,
  pipe,
  string,
  type InferOutput,
} from "valibot";
import { DateTime, DateZone, luxonDTValiType } from "@dmca/utils/luxon";
import { DEFAULT_AVG_WORK_TIME_SECONDS } from "@dmca/utils/work-time";
import { valibotResolver } from "@hookform/resolvers/valibot";
import {
  CellFeaturesEnum,
  type ICellLocation,
} from "@dmca/models/interface/cell-location";
import type { IProductionJobWithLocation } from "@dmca/models/interface/production-job";
import { Button } from "#components/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTRPC } from "#trpc";
import { useAppPermissions } from "#hooks/useAppPermissions";

export interface UpdateProductionJobModalProps extends ModalProps {
  job: IProductionJobWithLocation;
  cells: ICellLocation[];
}

const ModalFormSchema = object({
  dueDate: nullable(luxonDTValiType()),
  description: optional(string()),
  extraInfo: optional(
    pipe(string(), maxLength(20, "Extra Info must be 20 characters or less."))
  ),
  priority: pipe(
    number(),
    check(
      (x) => (x >= 1 && x <= 5) || x === 9,
      "Priority must be between 1-5 or 9 for hold."
    )
  ),
  priorityOverrideLocations: array(string()),
  genericTrackingLocations: array(string()),
  cellCompleteDate: nullable(luxonDTValiType()),
});
type ModalFormSchema = InferOutput<typeof ModalFormSchema>;

export const UpdateProductionJobModal: React.FC<
  UpdateProductionJobModalProps
> = ({ job, cells, onClose: userOnClose, open, ...rest }) => {
  // useDefaultProps, useRouter, useTheme, useQuery, ...
  const qc = useQueryClient();
  const proxy = useTRPC();
  const hasPermission = useAppPermissions();
  const canUpdateJob = hasPermission("ProductionJob::UpdateJob");
  const { data: sharedCellIds } = useQuery({
    ...proxy.productionJob.getSharedCellIds.queryOptions({ jobId: job.id }),
    enabled: open && canUpdateJob,
  });
  const [sharedCells, setSharedCells] = useState<string[]>([]);
  const initializedSharingFor = useRef<string | null>(null);
  const initializedFormFor = useRef<string | null>(null);
  const [workTimeSeconds, setWorkTimeSeconds] = useState<number | null>(
    job.avgWorkTimeSeconds ?? null
  );
  const averageChanged =
    canUpdateJob && workTimeSeconds !== (job.avgWorkTimeSeconds ?? null);
  const workTimeParts = splitWorkTime(
    workTimeSeconds ?? DEFAULT_AVG_WORK_TIME_SECONDS
  );
  const setWorkTimePart = (part: WorkTimePart, value: string) =>
    setWorkTimeSeconds((current) =>
      updateWorkTimePart(
        current ?? DEFAULT_AVG_WORK_TIME_SECONDS,
        part,
        Number(value) || 0
      )
    );
  useEffect(() => {
    if (!open) {
      initializedSharingFor.current = null;
    } else if (sharedCellIds && initializedSharingFor.current !== job.id) {
      setSharedCells(sharedCellIds);
      initializedSharingFor.current = job.id;
    }
  }, [open, job.id, sharedCellIds]);
  const sharingChanged =
    canUpdateJob &&
    sharedCellIds !== undefined &&
    JSON.stringify([...sharedCells].sort()) !==
      JSON.stringify([...sharedCellIds].sort());
  const updateJob = useMutation(
    proxy.productionJob.updateProductionJob.mutationOptions({
      onSuccess: () => {
        toast.success("Job updated.");
        qc.invalidateQueries(
          proxy.productionJob.getAllWithLocationPaginated.pathFilter()
        );
        qc.invalidateQueries(
          proxy.productionJob.getSharedCellIds.queryFilter()
        );
        qc.invalidateQueries(
          proxy.productionJob.getSmartCellJobs.queryFilter()
        );
        qc.invalidateQueries(
          proxy.productionJob.getSmartCellJobCellIds.queryFilter()
        );
      },
      onError: (error) => toast.error(error.message),
    })
  );

  // useState, useActiveState, useRef, ...
  const {
    control,
    handleSubmit: useFormHandleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<ModalFormSchema>({
    resolver: valibotResolver(ModalFormSchema),
    defaultValues: {
      dueDate: job?.dueDate ?? null,
      description: job?.name ?? "",
      priority: job?.priority ?? 5,
      extraInfo: job?.extraInfo ?? "",
      priorityOverrideLocations: job?.priorityOverrideLocations ?? [],
      genericTrackingLocations: job?.genericTrackingLocations ?? [],
      cellCompleteDate: job?.cellCompleteDate ?? null,
    },
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showReopenConfirmation, setShowReopenConfirmation] = useState(false);
  const hasChanges = isDirty || sharingChanged || averageChanged;
  const isSaving = updateJob.isPending;

  // useMemo

  const handleSubmit = async (data: ModalFormSchema) => {
    if (
      !canUpdateJob ||
      !hasChanges ||
      isSaving ||
      (averageChanged && workTimeSeconds === 0)
    )
      return;
    try {
      await updateJob.mutateAsync([
        {
          job: job.id,
          ...(isDirty
            ? {
                dueDate: data.dueDate?.setZone(DateZone.instance) as
                  | DateTime<true>
                  | null
                  | undefined,
                name: data.description || undefined,
                priority: data.priority,
                extraInfo: data.extraInfo || "",
                cellCompleteDate: data.cellCompleteDate
                  ? (data.cellCompleteDate.setZone(
                      DateZone.instance
                    ) as DateTime<true>)
                  : null,
                priorityOverrideLocations: (
                  data.priorityOverrideLocations ?? []
                ).map((x) => {
                  const cell = cells?.find((y) => y.locationName === x)?.cellId;
                  if (!cell) {
                    const err = new Error(`${x} is not a valid location.`, {
                      cause: "priorityOverrideLocations",
                    });
                    throw err;
                  }
                  return cell;
                }),
                genericTrackingLocations: (
                  data.genericTrackingLocations ?? []
                ).map((x) => {
                  const cell = cells?.find((y) => y.locationName === x)?.cellId;
                  if (!cell) {
                    const err = new Error(`${x} is not a valid location.`, {
                      cause: "genericTrackingLocations",
                    });
                    throw err;
                  }
                  return cell;
                }),
              }
            : {}),
          ...(sharingChanged ? { sharedCellIds: sharedCells } : {}),
          ...(averageChanged ? { avgWorkTimeSeconds: workTimeSeconds } : {}),
        },
      ]);
      onClose({}, "escapeKeyDown");
    } catch {
      // The mutation's onError displays the failure; the form remains open.
    }
  };

  const handleReset = (e: FormEvent) => {
    e.preventDefault();
    reset();
    setSharedCells(sharedCellIds ?? []);
    setWorkTimeSeconds(job.avgWorkTimeSeconds ?? null);
    onClose({}, "escapeKeyDown");
  };

  const onClose = (
    event: object,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    userOnClose?.(event, reason);
  };

  useEffect(() => {
    if (!open) {
      initializedFormFor.current = null;
    } else if (job && cells && initializedFormFor.current !== job.id) {
      reset({
        dueDate: job.dueDate ?? null,
        description: job.name ?? "",
        priority: job.priority,
        extraInfo: job.extraInfo ?? "",
        cellCompleteDate: job.cellCompleteDate ?? null,
        priorityOverrideLocations: job.priorityOverrideLocations
          .map((x) => cells.find((y) => y.cellId === x)!.locationName)
          .sort(),
        genericTrackingLocations: job.genericTrackingLocations
          .map((x) => cells.find((y) => y.cellId === x)!.locationName)
          .sort(),
      });
      setWorkTimeSeconds(job.avgWorkTimeSeconds ?? null);
      initializedFormFor.current = job.id;
    }
  }, [open, job, cells, reset]);

  // return
  return (
    <Modal
      data-testid="update-work-order-modal"
      header={`Update Work Order ${job.id}`}
      open={open}
      onClose={onClose}
      lazyLoad={false}
      {...rest}
      fullWidth
    >
      {!job?.completedDate && (
        <Alert severity="warning" sx={{ width: "100%" }}>
          Changing the due date will prevent COOIS from updating the due date
          automatically! This cannot be undone!
        </Alert>
      )}
      <Box
        component="form"
        onSubmit={useFormHandleSubmit(handleSubmit)}
        onReset={handleReset}
      >
        <Grid container columnSpacing={1}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="dueDate"
              control={control}
              render={({ field: { value, ...field } }) => (
                <DatePickerInput
                  data-testid="updateProductionJobModal-dueDate"
                  label="Due Date"
                  value={value ?? null}
                  readOnly={!!job?.completedDate}
                  {...field}
                  disabled={!canUpdateJob}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="description"
              control={control}
              disabled={!canUpdateJob}
              inputProps={{ label: "Description" }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Input
              label="Project"
              value={job.project ?? "MERCH/STOCK"}
              disabled={true}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="priority"
              control={control}
              disabled={!canUpdateJob}
              transform="number"
              inputProps={{
                label: "Priority",
                required: true,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="extraInfo"
              control={control}
              disabled={!canUpdateJob}
              inputProps={{
                helperText: "Max 20 characters",
                label: "Extra Info",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="cellCompleteDate"
              control={control}
              render={({ field: { value, ...field } }) => (
                <DatePickerInput
                  data-testid="updateProductionJobModal-cellCompleteDate"
                  label="Cell Complete Date"
                  value={value ?? null}
                  readOnly={!!job?.completedDate}
                  {...field}
                  disabled={!canUpdateJob}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="priorityOverrideLocations"
              control={control}
              render={({ field: { onChange, ...rest }, fieldState }) => (
                <AutoComplete
                  options={(cells ?? [])
                    .filter((x) =>
                      x.featureFlag?.includes(CellFeaturesEnum.RFID)
                    )
                    .map((x) => x.locationName)
                    .sort()}
                  onChange={(_, newValue) => {
                    onChange(newValue);
                  }}
                  {...rest}
                  disabled={!canUpdateJob}
                  label="Critical Locations"
                  inputProps={{
                    error: !!fieldState.error,
                    errorText: fieldState.error?.message,
                  }}
                  multiple
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="genericTrackingLocations"
              control={control}
              render={({ field: { onChange, ...rest }, fieldState }) => (
                <AutoComplete
                  options={(cells ?? [])
                    .filter((x) =>
                      x.featureFlag.includes(CellFeaturesEnum.RFID)
                    )
                    .map((x) => x.locationName)
                    .sort()}
                  {...rest}
                  disabled={!canUpdateJob}
                  onChange={(_, newValue) => {
                    onChange(newValue);
                  }}
                  label="Generic Tracking Locations"
                  inputProps={{
                    error: !!fieldState.error,
                    errorText: fieldState.error?.message,
                  }}
                  multiple
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {job?.completedDate && (
            <Grid size={{ xs: 12, md: "auto" }}>
              <h3>Completed Date</h3>
              <span>
                {job?.completedDate?.toLocaleString(DateTime.DATETIME_FULL)}
              </span>
            </Grid>
          )}
        </Grid>

        {canUpdateJob && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
            <AutoComplete
              options={cells
                .filter((cell) =>
                  cell.featureFlag.includes(CellFeaturesEnum.RFID)
                )
                .map((cell) => cell.locationName)
                .sort()}
              value={sharedCells.map(
                (cellId) =>
                  cells.find((cell) => cell.cellId === cellId)?.locationName ??
                  cellId
              )}
              onChange={(_, names) =>
                setSharedCells(
                  names.map(
                    (name) =>
                      cells.find((cell) => cell.locationName === name)!.cellId
                  )
                )
              }
              label="Share WO with cells"
              multiple
              disabled={sharedCellIds === undefined}
              sx={{ flexGrow: 1 }}
            />
          </Stack>
        )}

        {canUpdateJob && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Typography fontWeight={600}>WO average work time</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={workTimeSeconds === null}
                  onChange={(_, checked) =>
                    setWorkTimeSeconds(
                      checked ? null : DEFAULT_AVG_WORK_TIME_SECONDS
                    )
                  }
                />
              }
              label="Use each cell's average"
            />
            {workTimeSeconds !== null && (
              <WorkTimeFields
                value={workTimeParts}
                onChange={setWorkTimePart}
              />
            )}
            {workTimeSeconds === 0 && (
              <Typography color="error" variant="caption">
                Average work time must be greater than zero.
              </Typography>
            )}
          </Stack>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            flexWrap: "wrap",
            mt: 3,
            "& .MuiButton-root": {
              flexGrow: { xs: 1, sm: 0 },
            },
          }}
        >
          {canUpdateJob && job && !job.completedDate && (
            <Button
              data-testid="jobs-overview-mark-task-finished"
              color="success"
              onClick={() => setShowConfirmation(true)}
            >
              Mark task as finished
            </Button>
          )}

          {canUpdateJob && job && job.completedDate && (
            <Button
              data-testid="jobs-overview-reopen-task"
              color="warning"
              onClick={() => setShowReopenConfirmation(true)}
            >
              Reopen task
            </Button>
          )}

          <Stack
            direction="row"
            gap={1}
            sx={{
              width: { xs: "100%", sm: "auto" },
              py: { xs: 1, sm: 0 },
            }}
          >
            <Button
              color="error"
              data-testid="jobs-overview-cancel-btn"
              type="reset"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              data-testid="jobs-overview-job-update-form"
              color="success"
              type="submit"
              disabled={!canUpdateJob || !hasChanges || isSaving}
              loading={isSaving}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Box>

      <ConfirmationModal
        //Confirmation for Marking Task Finished
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        message="Mark Task as finished? It will move out of the active job list. You can reopen it later from completed jobs."
        onConfirm={() => {
          updateJob.mutate([{ job: job.id, isCompleted: true }], {
            onSuccess: () => onClose({}, "escapeKeyDown"),
          });
        }}
        title=""
      />

      <ConfirmationModal
        //Confirmation for Reopening Task
        open={showReopenConfirmation}
        onClose={() => setShowReopenConfirmation(false)}
        message="Reopen this work order? It will return to the active job list."
        onConfirm={() => {
          updateJob.mutate([{ job: job.id, isCompleted: false }], {
            onSuccess: () => {
              setShowReopenConfirmation(false);
              onClose({}, "escapeKeyDown");
            },
          });
        }}
        title=""
      />
    </Modal>
  );
};
